class LiveAtomicEmailPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || this.events) return;
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.busy = new Set();
    this.remaining = (Number(this.dataset.delay) || 15) * 1000;
    this.spent = false;
    this.querySelectorAll('[data-email-close]').forEach(button => button.addEventListener('click', () => this.dismiss(), options));
    this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.dismiss(); }, options);
    this.dialog.addEventListener('close', () => this.restorePage(), options);
    this.querySelector('[data-email-form]').addEventListener('submit', event => this.submit(event, 'email'), options);
    this.querySelector('[data-email-sms-form]')?.addEventListener('submit', event => this.submit(event, 'sms'), options);
    document.addEventListener('click', event => {
      if (event.target instanceof Element && event.target.closest('[data-email-open]')) this.show();
    }, options);
    document.addEventListener('visibilitychange', () => this.syncTimer(), options);
    document.addEventListener('shopify:section:select', event => {
      if (event.target.contains(this)) this.show();
    }, options);
    document.querySelectorAll('[data-email-open]').forEach(button => { button.hidden = false; });
    if (this.readMemory().subscribed) this.showSuccess(false);
    if (this.querySelector('[data-email-accepted]')) {
      this.remember({ subscribed: true, dismissedAt: Date.now() });
      this.showSuccess(false);
      this.show();
    }
    const serverError = this.querySelector('[data-email-server-error]');
    if (serverError) {
      const status = this.querySelector('#AtomicEmailError');
      status.textContent = serverError.textContent;
      status.hidden = false;
      this.show();
    }
    if (this.readMemory().smsSubscribed) this.showSmsSuccess();
    this.syncTimer();
  }

  disconnectedCallback() {
    this.events?.abort();
    this.events = null;
    window.clearTimeout(this.timer);
    this.timer = 0;
    if (this.dialog?.open) this.dialog.close();
    this.restorePage();
  }

  readMemory() {
    try {
      const value = JSON.parse(localStorage.getItem('la:launch-list') || '{}');
      return value && typeof value === 'object' ? value : {};
    } catch { return {}; }
  }

  remember(patch) {
    try { localStorage.setItem('la:launch-list', JSON.stringify({ ...this.readMemory(), ...patch })); }
    catch { /* The form still works when storage is unavailable. */ }
  }

  syncTimer() {
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = 0;
      this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt));
    }
    const memory = this.readMemory();
    if (this.spent || this.dataset.editor === 'true' || memory.subscribed ||
      (memory.dismissedAt && Date.now() - memory.dismissedAt < 30 * 86400000) ||
      document.visibilityState !== 'visible' || /^\/(cart|checkout|checkouts|account|policies)(\/|$)/.test(location.pathname)) return;
    this.startedAt = Date.now();
    this.timer = window.setTimeout(() => {
      this.timer = 0;
      // Do not interrupt an open cart or another modal.
      if (document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]')) {
        this.remaining = 1000;
        this.syncTimer();
      } else this.show();
    }, this.remaining);
  }

  show() {
    if (this.dialog.open) return;
    this.spent = true;
    window.clearTimeout(this.timer);
    this.timer = 0;
    this.restoreFocus = document.activeElement;
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.dialog.showModal();
    this.dialog.scrollTop = 0;
    this.querySelector('[data-email-close]').focus();
  }

  dismiss() {
    this.remember({ dismissedAt: Date.now() });
    this.dialog.close();
  }

  restorePage() {
    if (this.previousOverflow === undefined) return;
    document.body.style.overflow = this.previousOverflow;
    this.previousOverflow = undefined;
    if (this.restoreFocus?.isConnected) this.restoreFocus.focus();
  }

  async subscribeSms(value) {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 10) value = `+1${digits}`;
    else if (digits.length === 11 && digits.startsWith('1')) value = `+${digits}`;
    else throw new Error('Enter a valid US or Canadian mobile number.');
    if (!this.dataset.companyId || !this.dataset.smsListId) throw new Error('Text signup is unavailable right now. Please try again later.');
    let response;
    try {
      response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
        method: 'POST', signal: this.events.signal,
        headers: { Accept: 'application/vnd.api+json', 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
        body: JSON.stringify({ data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Live Atomic Shopify email popup',
            profile: { data: { type: 'profile', attributes: {
              phone_number: value, subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } },
            } } },
          },
          relationships: { list: { data: { type: 'list', id: this.dataset.smsListId } } },
        } }),
      });
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      throw new Error('We could not add text updates yet. Please try again in a minute.');
    }
    if (response.status !== 202) throw new Error('We could not add text updates yet. Please try again in a minute.');
  }

  async submit(event, channel) {
    const form = event.currentTarget;
    if (channel === 'email') {
      // Let Shopify complete its CAPTCHA and native customer-form submission.
      // Only the returned Liquid success marker confirms email signup.
      if (String(form.elements.namedItem('company')?.value || '').trim()) event.preventDefault();
      return;
    }
    event.preventDefault();
    if (this.busy.has(channel)) return;
    const input = form.elements.namedItem(channel === 'email' ? 'contact[email]' : 'phone');
    const status = this.querySelector(channel === 'email' ? '#AtomicEmailError' : '#AtomicSmsStatus');
    const button = form.querySelector('button[type="submit"]');
    const label = button.textContent;
    this.busy.add(channel);
    button.disabled = true;
    button.textContent = 'Sending…';
    form.setAttribute('aria-busy', 'true');
    status.hidden = true;
    input.removeAttribute('aria-invalid');
    try {
      await this.subscribeSms(input.value);
      this.remember({ smsSubscribed: true });
      this.showSmsSuccess();
      form.reset();
    } catch (error) {
      if (error.name === 'AbortError') return;
      status.textContent = error instanceof TypeError ? 'We could not add you yet. Please try again in a minute.' : error.message;
      status.hidden = false;
      input.setAttribute('aria-invalid', 'true');
    } finally {
      this.busy.delete(channel);
      button.disabled = false;
      button.textContent = label;
      form.removeAttribute('aria-busy');
    }
  }

  showSuccess(focus = true) {
    this.querySelector('[data-email-entry]').hidden = true;
    this.querySelector('[data-email-success]').hidden = false;
    this.querySelector('[data-email-eyebrow]').textContent = 'The Focus Protocol';
    const title = this.querySelector('#AtomicEmailTitle');
    title.textContent = "You're on the list.";
    this.querySelector('#AtomicEmailBody').textContent = 'Check your inbox for confirmation and updates from Live Atomic.';
    if (focus && this.dialog.open) title.focus();
  }

  showSmsSuccess() {
    const form = this.querySelector('[data-email-sms-form]');
    if (!form) return;
    form.hidden = true;
    const status = this.querySelector('#AtomicSmsStatus');
    status.classList.remove('la-email-modal__error');
    status.hidden = false;
    status.textContent = 'Check your phone. Confirm by text to finish signing up for updates.';
  }
}

if (!customElements.get('live-atomic-email-popup')) customElements.define('live-atomic-email-popup', LiveAtomicEmailPopup);
